// Hand-written validation runtime for @beacon/lexicon.
//
// Imported by the generated validators. Pure and clock-free (ADR-13): every
// check here is a regex or arithmetic test — never Date.now / new Date(...).
// The 8 format regexes are transcribed verbatim from lexicons/CONVENTIONS.md;
// they must stay byte-identical to the Go and Rust runtimes so all three
// targets agree on the shared fixtures.

export type FieldError = { path: string; rule: string; message: string };
export type Result = { ok: true } | { ok: false; errors: FieldError[] };
export type EventResult = { ok: boolean; known: boolean; errors: FieldError[] };

// --- datetime: RFC 3339, UTC only, plus a real calendar-date check ---
const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}(\.\d{1,9})?Z$/;

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function validDatetime(s: string): boolean {
  const m = DATETIME_RE.exec(s);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) return false;
  const dim = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day < 1 || day > dim[month - 1]) return false;
  return true;
}

// --- h3-cell: 15 lowercase hex chars, mode bits = cell (high nibble 8 => first char '8') ---
const H3_RE = /^[0-9a-f]{15}$/;

function validH3Cell(s: string): boolean {
  if (!H3_RE.test(s)) return false;
  // High nibble (bits 60-63) of the u64 is the leading hex char; cell mode = 8.
  return s[0] === "8";
}

/**
 * Resolution of an H3 cell = the 4-bit nibble at bits 52-55 of the u64, which is
 * the second hex character of the 15-char string. Returned as 0-15.
 */
export function h3Res(cell: string): number {
  return parseInt(cell[1], 16);
}

export const FORMATS: Record<string, (s: string) => boolean> = {
  tid: (s) => /^[234567abcdefghij][234567abcdefghijklmnopqrstuvwxyz]{12}$/.test(s),
  nsid: (s) => /^[a-zA-Z]([a-zA-Z0-9-]{0,62})(\.[a-zA-Z]([a-zA-Z0-9-]{0,62})){2,}$/.test(s),
  did: (s) =>
    /^did:beacon:[a-z2-7]{26}$/.test(s) ||
    /^did:key:z[1-9A-HJ-NP-Za-km-z]{8,256}$/.test(s),
  "did-key": (s) => /^did:key:z[1-9A-HJ-NP-Za-km-z]{8,256}$/.test(s),
  datetime: validDatetime,
  "h3-cell": validH3Cell,
  bcp47: (s) => /^[a-zA-Z]{2,8}(-[a-zA-Z0-9]{1,8})*$/.test(s),
  "tid-or-did": (s) => FORMATS.tid(s) || FORMATS.did(s),
};

// --- bytes JSON form: {"$bytes": "<base64, standard padding>"} ---

export function isBytes(v: unknown): v is { $bytes: string } {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { $bytes?: unknown }).$bytes === "string"
  );
}

const BASE64_RE = /^[A-Za-z0-9+/]*={0,2}$/;

/**
 * Decoded byte length of a {$bytes} value, computed from the string length
 * without allocating the decoded buffer. Returns -1 if the payload is not valid
 * standard-alphabet base64 (wrong alphabet, or length not a multiple of 4).
 */
export function bytesLen(v: { $bytes: string }): number {
  const s = v.$bytes;
  if (!BASE64_RE.test(s)) return -1;
  if (s.length % 4 !== 0) return -1;
  if (s.length === 0) return 0;
  let pad = 0;
  if (s[s.length - 1] === "=") pad++;
  if (s[s.length - 2] === "=") pad++;
  return (s.length / 4) * 3 - pad;
}
